// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Table component for displaying assets with detail inconsistencies.
 *
 * Shows assets with missing or incorrect detail information such as
 * cost basis, salvage value, or useful life issues. Uses react-window
 * for virtualized rendering to handle large datasets efficiently.
 */
import { EmptyState } from '@/components/empty-state';
import { Link } from '@/components/link';
import { TruncatedText } from '@/components/truncated-text';
import { CheckCircleOutlined } from '@/constants/hardfin-standard-icons';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { DetailInconsistencyItem } from '@/report/api/types';
import { AssetExceptionResolutionDialog } from '@/report/components/asset-exception-resolution-dialog';
import { getExceptionConfig } from '@/report/utilities/exception-config';
import { pluralize } from '@/utilities/pluralize';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

// Height of each row in pixels for virtualization
const ROW_HEIGHT = 57;

// Number of rows to pre-render outside the visible area
const OVERSCAN_COUNT = 10;

// Maximum height for the virtualized table body
const MAX_TABLE_HEIGHT = 800;

interface Props {
  assets: DetailInconsistencyItem[];
}

// Data passed to the virtualized row renderer
interface RowData {
  assets: DetailInconsistencyItem[];
  onSelectAsset: (item: DetailInconsistencyItem) => void;
  resolvedAssetIds: Set<string>;
}

// Virtualized row renderer for the detail inconsistencies table
function VirtualizedRow(props: ListChildComponentProps<RowData>) {
  const item = props.data.assets[props.index];
  const isResolved = props.data.resolvedAssetIds.has(item.asset.id);

  return (
    <Box
      style={props.style}
      sx={{
        display: 'grid',
        gridTemplateColumns:
          'minmax(150px, 1.5fr) repeat(5, minmax(100px, 1fr)) minmax(120px, 0.5fr)',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
        bgcolor: isResolved ? 'success.50' : 'transparent',
      }}
    >
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        <Link
          to="/assets/$assetKey"
          color="primary.main"
          variant="subtitle2"
          params={{ assetKey: item.asset.key }}
        >
          {item.asset.serial}
        </Link>
        <TruncatedText
          text={`${item.asset.item.sku} (${item.asset.item.name})`}
          variant="caption"
          color="text.secondary"
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <Typography variant="body2">
          {item.inServiceDate
            ? item.inServiceDate.toLocaleString({ dateStyle: 'medium' })
            : '—'}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <ValueCellWithException
          value={
            item.usefulLife !== null
              ? pluralize(item.usefulLife, {
                  singular: 'month',
                  plural: 'months',
                })
              : null
          }
          exceptionType={
            isResolved || !item.exceptionTypes.includes('MISSING_USEFUL_LIFE')
              ? null
              : 'MISSING_USEFUL_LIFE'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <ValueCellWithException
          value={
            item.inventoryCostBasis !== null
              ? formatAmountWithCurrency(
                  item.inventoryCostBasis,
                  item.costCurrencyCode
                )
              : null
          }
          exceptionType={
            isResolved || !item.exceptionTypes.includes('MISSING_COST_BASIS')
              ? null
              : 'MISSING_COST_BASIS'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <ValueCellWithException
          value={
            item.deployedCostBasis !== null
              ? formatAmountWithCurrency(
                  item.deployedCostBasis,
                  item.costCurrencyCode
                )
              : null
          }
          exceptionType={
            isResolved ||
            !item.exceptionTypes.includes('DEPLOYMENT_WITHOUT_ACQUISITION')
              ? null
              : 'DEPLOYMENT_WITHOUT_ACQUISITION'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        <ValueCellWithException
          value={
            item.salvageValue !== null
              ? formatAmountWithCurrency(
                  item.salvageValue,
                  item.costCurrencyCode
                )
              : null
          }
          exceptionType={
            isResolved ||
            !item.exceptionTypes.includes('SALVAGE_EXCEEDS_50_PERCENT')
              ? null
              : 'SALVAGE_EXCEEDS_50_PERCENT'
          }
        />
      </Box>
      <Box sx={{ textAlign: 'right', py: 1 }}>
        {isResolved ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}
          >
            <CheckCircleOutlined
              fontSize="small"
              sx={{ color: 'success.main' }}
            />
            <Typography variant="body2" color="success.main">
              Resolved
            </Typography>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={() => props.data.onSelectAsset(item)}
          >
            Resolve
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * Renders a value cell with an optional exception indicator.
 * Values are right-aligned for better scanning, and exceptions are shown
 * as a subtle icon badge with a tooltip for full details.
 */
function ValueCellWithException(props: {
  value: string | number | null;
  exceptionType: string | null;
}) {
  const valueText = props.value !== null ? String(props.value) : '—';

  if (!props.exceptionType) {
    return (
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {valueText}
      </Typography>
    );
  }

  const config = getExceptionConfig(props.exceptionType);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.75,
      }}
    >
      <Tooltip title={config.label} arrow placement="top">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.75}
          sx={{
            cursor: 'help',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'error.main',
            }}
          >
            {config.icon}
          </Box>
          <Typography
            variant="subtitle2"
            color="error.dark"
            sx={{ textAlign: 'right' }}
          >
            {valueText}
          </Typography>
        </Stack>
      </Tooltip>
    </Box>
  );
}

export function DetailInconsistenciesTable(props: Props) {
  const [selectedAsset, setSelectedAsset] =
    useState<DetailInconsistencyItem | null>(null);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [resolvedAssetIds, setResolvedAssetIds] = useState<Set<string>>(
    new Set()
  );

  // Memoized callback to handle asset selection for the resolve dialog
  const handleSelectAsset = useCallback((item: DetailInconsistencyItem) => {
    setSelectedAsset(item);
    setIsResolveModalOpen(true);
  }, []);

  // Handler for when an asset's exceptions are resolved
  // Adds the asset ID to the resolved set for optimistic UI update
  const handleAssetResolved = useCallback((assetId: string) => {
    setResolvedAssetIds((prev) => new Set(prev).add(assetId));
  }, []);

  // Calculate the height for the virtualized list
  const listHeight = Math.min(
    props.assets.length * ROW_HEIGHT,
    MAX_TABLE_HEIGHT
  );

  if (props.assets.length === 0) {
    return (
      <>
        <EmptyState
          title="No detail inconsistencies found"
          message="All assets have complete and consistent detail information"
        />
        {/* Keep dialog mounted so it can show resolved state when user returns */}
        <AssetExceptionResolutionDialog
          open={isResolveModalOpen}
          onClose={() => setIsResolveModalOpen(false)}
          onExited={() => setSelectedAsset(null)}
          onResolved={handleAssetResolved}
          asset={selectedAsset?.asset}
          exceptionType="detail"
          exceptions={selectedAsset?.exceptionTypes}
          detailData={
            selectedAsset
              ? {
                  inServiceDate: selectedAsset.inServiceDate,
                  usefulLife: selectedAsset.usefulLife,
                  inventoryCostBasis: selectedAsset.inventoryCostBasis,
                  deployedCostBasis: selectedAsset.deployedCostBasis,
                  salvageValue: selectedAsset.salvageValue,
                }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <Paper variant="outlined">
      {/* Header row using CSS grid to match virtualized rows */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns:
            'minmax(150px, 1.5fr) repeat(5, minmax(100px, 1fr)) minmax(120px, 0.5fr)',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.100',
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="overline">Serial</Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          In service
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Useful life
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Inventory cost
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Deployed cost
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Salvage value
        </Typography>
        <Typography variant="overline" sx={{ textAlign: 'right' }}>
          Actions
        </Typography>
      </Box>

      {/* Virtualized table body */}
      <FixedSizeList
        height={listHeight}
        width="100%"
        itemSize={ROW_HEIGHT}
        itemCount={props.assets.length}
        overscanCount={OVERSCAN_COUNT}
        itemData={{
          assets: props.assets,
          onSelectAsset: handleSelectAsset,
          resolvedAssetIds,
        }}
      >
        {VirtualizedRow}
      </FixedSizeList>

      <AssetExceptionResolutionDialog
        open={isResolveModalOpen}
        onClose={() => setIsResolveModalOpen(false)}
        onExited={() => setSelectedAsset(null)}
        onResolved={handleAssetResolved}
        asset={selectedAsset?.asset}
        exceptionType="detail"
        exceptions={selectedAsset?.exceptionTypes}
        detailData={
          selectedAsset
            ? {
                inServiceDate: selectedAsset.inServiceDate,
                usefulLife: selectedAsset.usefulLife,
                inventoryCostBasis: selectedAsset.inventoryCostBasis,
                deployedCostBasis: selectedAsset.deployedCostBasis,
                salvageValue: selectedAsset.salvageValue,
              }
            : undefined
        }
      />
    </Paper>
  );
}
